import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { webApi } from '../services/api';
import { isCanceledRequest } from '../utils/abortRequest';
import { formatDateYYYYMMDDIST } from '../utils/dateDisplay';
import { resolvedAvatarUrl, isDicebearUrl } from '../lib/dicebearAvatar';

const SWIPE_UNLINK_PX = 52;

function TrashUnlinkIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" className="emp-row-unlink-svg">
      <path
        fill="currentColor"
        d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM8 9h2v9H8V9zm6 0h2v9h-2V9zm5-5h-3.5l-1-1h-5l-1 1H5v2h14V4z"
      />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"
      />
    </svg>
  );
}

function EmployeeListAvatar({ src, name, seed }) {
  const [broken, setBroken] = useState(false);
  const url = resolvedAvatarUrl(src, seed, name);
  const dice = isDicebearUrl(url);
  if (url && !broken) {
    return (
      <img
        className={dice ? 'emp-list-avatar-img emp-list-avatar-img--dicebear' : 'emp-list-avatar-img'}
        src={url}
        alt=""
        onError={() => setBroken(true)}
      />
    );
  }
  const initial = (name && name.trim()[0]) || '?';
  return (
    <span className="emp-list-avatar-img emp-list-avatar-img--placeholder" aria-hidden="true">
      {initial.toUpperCase()}
    </span>
  );
}

function clamp(n, lo, hi) {
  return Math.max(lo, Math.min(hi, n));
}

function SwipeableEmployeeRow({
  employee,
  swipeOpenId,
  setSwipeOpenId,
  onUnlinkRequest
}) {
  const [offset, setOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const offsetRef = useRef(0);
  const draggingRef = useRef(false);
  const startClientXRef = useRef(0);
  const baseOffsetRef = useRef(0);
  const frontRef = useRef(null);

  const isOpen = swipeOpenId === employee.id;

  useEffect(() => {
    offsetRef.current = offset;
  }, [offset]);

  useEffect(() => {
    if (!draggingRef.current) {
      const target = isOpen ? SWIPE_UNLINK_PX : 0;
      offsetRef.current = target;
      setOffset(target);
    }
  }, [isOpen]);

  const endDragSnap = useCallback(() => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    setIsDragging(false);
    const next = offsetRef.current;
    const snapOpen = next > SWIPE_UNLINK_PX / 2;
    const final = snapOpen ? SWIPE_UNLINK_PX : 0;
    offsetRef.current = final;
    setOffset(final);
    if (snapOpen) setSwipeOpenId(employee.id);
    else setSwipeOpenId((id) => (id === employee.id ? null : id));
  }, [employee.id, setSwipeOpenId]);

  const onTouchStart = (e) => {
    if (swipeOpenId != null && swipeOpenId !== employee.id) {
      setSwipeOpenId(null);
    }
    draggingRef.current = true;
    setIsDragging(true);
    startClientXRef.current = e.touches[0].clientX;
    baseOffsetRef.current = offsetRef.current;
  };

  const onTouchMove = (e) => {
    if (!draggingRef.current) return;
    const x = e.touches[0].clientX;
    const d = startClientXRef.current - x;
    const next = clamp(baseOffsetRef.current + d, 0, SWIPE_UNLINK_PX);
    offsetRef.current = next;
    setOffset(next);
  };

  const onTouchEnd = () => {
    endDragSnap();
  };

  const onTouchCancel = () => {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    setIsDragging(false);
    const revert = swipeOpenId === employee.id ? SWIPE_UNLINK_PX : 0;
    offsetRef.current = revert;
    setOffset(revert);
  };

  useEffect(() => {
    const el = frontRef.current;
    if (!el) return undefined;
    const move = (e) => {
      if (!draggingRef.current || !e.touches[0]) return;
      const d = startClientXRef.current - e.touches[0].clientX;
      if (Math.abs(d) > 6) e.preventDefault();
    };
    el.addEventListener('touchmove', move, { passive: false });
    return () => el.removeEventListener('touchmove', move);
  }, []);

  const mouseDownRef = useRef(false);

  const onMouseDown = (e) => {
    if (e.button !== 0) return;
    if (swipeOpenId != null && swipeOpenId !== employee.id) {
      setSwipeOpenId(null);
    }
    mouseDownRef.current = true;
    draggingRef.current = true;
    setIsDragging(true);
    startClientXRef.current = e.clientX;
    baseOffsetRef.current = offsetRef.current;
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!mouseDownRef.current || !draggingRef.current) return;
      const d = startClientXRef.current - e.clientX;
      const next = clamp(baseOffsetRef.current + d, 0, SWIPE_UNLINK_PX);
      offsetRef.current = next;
      setOffset(next);
    };
    const onUp = () => {
      if (!mouseDownRef.current) return;
      mouseDownRef.current = false;
      endDragSnap();
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [endDragSnap]);

  return (
    <li className="emp-swipe-item">
      <div className="emp-swipe-track" role="presentation">
        <div className="emp-swipe-behind">
          <button
            type="button"
            className="emp-row-unlink"
            aria-label={`Unlink ${employee.name || 'employee'}`}
            onClick={(e) => {
              e.stopPropagation();
              onUnlinkRequest(employee);
              setSwipeOpenId(null);
              offsetRef.current = 0;
              setOffset(0);
            }}
          >
            <TrashUnlinkIcon />
          </button>
        </div>
        <div
          ref={frontRef}
          className="emp-swipe-front"
          style={{
            transform: `translateX(-${offset}px)`,
            transition: isDragging ? 'none' : 'transform 0.2s ease-out'
          }}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onTouchCancel={onTouchCancel}
          onMouseDown={onMouseDown}
        >
          <Link to={`/employees/${employee.id}`} className="emp-row" draggable={false}>
            <EmployeeListAvatar src={employee.profilePictureUrl} name={employee.name} seed={employee.id} />
            <div className="emp-row-main">
              <p className="emp-row-name">{employee.name || '—'}</p>
              <p className="emp-row-phone">{employee.phone || '—'}</p>
            </div>
            <div className="emp-row-meta">
              <span
                className={`emp-status-dot${employee.status === 'active' ? ' emp-status-dot--on' : ''}`}
                title={employee.status || ''}
                aria-hidden="true"
              />
              <time className="emp-row-date" dateTime={employee.createdAt}>
                {formatDateYYYYMMDDIST(employee.createdAt)}
              </time>
            </div>
          </Link>
        </div>
      </div>
    </li>
  );
}

function EmployeesPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [loadError, setLoadError] = useState('');
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unlinkTarget, setUnlinkTarget] = useState(null);
  const [unlinkSubmitting, setUnlinkSubmitting] = useState(false);
  const [unlinkError, setUnlinkError] = useState('');
  const [swipeOpenId, setSwipeOpenId] = useState(null);

  const loadEmployees = useCallback(async (signal) => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await webApi.getEmployees(signal ? { signal } : {});
      if (signal?.aborted) return;
      setEmployees(Array.isArray(data) ? data : []);
    } catch (err) {
      if (signal?.aborted || isCanceledRequest(err)) return;
      setLoadError(err.message || 'Failed to load employees');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    loadEmployees(ac.signal);
    return () => ac.abort();
  }, [loadEmployees]);

  useEffect(() => {
    if (swipeOpenId == null) return undefined;
    const close = (e) => {
      const el = e.target;
      if (el && typeof el.closest === 'function' && el.closest('.emp-swipe-track')) return;
      setSwipeOpenId(null);
    };
    document.addEventListener('click', close, true);
    return () => document.removeEventListener('click', close, true);
  }, [swipeOpenId]);

  const q = search.trim().toLowerCase();
  const filtered = q
    ? employees.filter((e) => (e.name || '').toLowerCase().includes(q))
    : employees;

  useEffect(() => {
    if (!unlinkTarget) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !unlinkSubmitting) setUnlinkTarget(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [unlinkTarget, unlinkSubmitting]);

  const confirmUnlink = async () => {
    if (!unlinkTarget?.id) return;
    setUnlinkSubmitting(true);
    setUnlinkError('');
    try {
      await webApi.unlinkEmployee({ employeeId: Number(unlinkTarget.id) });
      setUnlinkTarget(null);
      setSwipeOpenId(null);
      await loadEmployees();
    } catch (err) {
      setUnlinkError(err.message || 'Could not unlink employee');
    } finally {
      setUnlinkSubmitting(false);
    }
  };

  const onUnlinkRequest = useCallback((employee) => {
    setUnlinkError('');
    setUnlinkTarget({ id: employee.id, name: employee.name || 'Employee' });
  }, []);

  return (
    <div className="emp-page">
      <header className="emp-topbar">
        <button type="button" className="emp-back" onClick={() => navigate(-1)} aria-label="Go back">
          <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="currentColor" d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
          </svg>
        </button>
        <h1 className="emp-topbar-title">Your Employees</h1>
        <span className="emp-topbar-spacer" aria-hidden="true" />
      </header>

      <div className="emp-body">
        <label className="emp-search">
          <span className="emp-search-icon">
            <SearchIcon />
          </span>
          <input
            className="emp-search-input"
            type="search"
            placeholder="Search Employee Name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoComplete="off"
          />
        </label>

        {loadError ? (
          <p className="emp-banner emp-banner--error" role="alert">
            {loadError}
          </p>
        ) : null}

        {loading ? (
          <p className="emp-loading">Loading…</p>
        ) : filtered.length === 0 ? (
          <div className="emp-empty">
            <p className="emp-empty-title">{q ? 'No matches' : 'No employees yet'}</p>
            <p className="emp-empty-sub">
              {q ? 'Try another name.' : 'Employees linked to your business will show up here.'}
            </p>
          </div>
        ) : (
          <ul className="emp-list">
            {filtered.map((employee) => (
              <SwipeableEmployeeRow
                key={employee.id}
                employee={employee}
                swipeOpenId={swipeOpenId}
                setSwipeOpenId={setSwipeOpenId}
                onUnlinkRequest={onUnlinkRequest}
              />
            ))}
          </ul>
        )}
      </div>

      {unlinkTarget ? (
        <div
          className="emp-unlink-overlay"
          role="presentation"
          onClick={() => !unlinkSubmitting && setUnlinkTarget(null)}
        >
          <div
            className="emp-unlink-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="emp-unlink-title"
            onClick={(ev) => ev.stopPropagation()}
          >
            <p id="emp-unlink-title" className="emp-unlink-text">
              Are you sure you want to unlink <span className="emp-unlink-name">{unlinkTarget.name}</span>?
            </p>
            {unlinkError ? (
              <p className="emp-unlink-error" role="alert">
                {unlinkError}
              </p>
            ) : null}
            <div className="emp-unlink-actions">
              <button
                type="button"
                className="emp-unlink-cancel"
                disabled={unlinkSubmitting}
                onClick={() => setUnlinkTarget(null)}
              >
                Cancel
              </button>
              <button type="button" className="emp-unlink-sure" disabled={unlinkSubmitting} onClick={confirmUnlink}>
                {unlinkSubmitting ? '…' : 'Sure'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default EmployeesPage;
